import {Link} from '@jobseeker/lotus/elements';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import UploadInput from '@jobseeker/lotus/elements/UploadInput';
import Loader from '@jobseeker/lotus/components/Loader';
import {Box, Skeleton, Typography} from '@mui/material';
import {enqueueSnackbar} from 'notistack';
import {
  truncateString,
  getFileBlobFromUrl,
  getFileNameFromFileUrl,
  formatBytes,
} from '@jobseeker/lotus/utils';
import {
  BaseSyntheticEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {ResumeAttached, HiristResumeAttached} from '@jobseeker/lotus/icons';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type UploadFileProps = {
  title: string;
  buttonText: string;
  submitFile: boolean;
  changeFile?: boolean;
  handleSubmit: (formData: FormData) => Promise<void>;
  handleFileConfirm?: () => void;
  handleTracking?: () => void;
  formFileName?: string;
  initialFile?: string | null;
  defaultFileName?: string;
  uploadTimeStamp?: string;
  allowedFileSize?: number;
  allowedFileFormat?: string;
  alertMessage?: string;
  newUploadText?: string;
  downloadText?: string;
  nofileError?: boolean;
  profileStatus?: boolean;
};

const UploadFile = ({
  title,
  buttonText,
  submitFile,
  changeFile,
  handleSubmit,
  handleFileConfirm,
  formFileName = 'file',
  initialFile,
  defaultFileName,
  uploadTimeStamp,
  allowedFileSize = 2097152,
  allowedFileFormat = '.doc,.docx,.pdf',
  alertMessage = 'Accepted Format includes PDF, DOC & DOCX upto 2MB',
  newUploadText = 'Upload new resume',
  downloadText = 'Download',
  nofileError = false,
  profileStatus = false,
}: UploadFileProps) => {
  const NO_FILE_NAME = 'No file chosen';
  const [fileName, setFileName] = useState(NO_FILE_NAME);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<Blob>();
  const [error, setError] = useState<string>('');
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const hiddenFileReInput = useRef<HTMLInputElement>(null);
  const [sanitisedFileName, setSanitisedFileName] = useState('');

  useEffect(() => {
    setSanitisedFileName(
      `${defaultFileName}.${fileName.split('.').pop()?.toLowerCase()}`
    );
  }, [defaultFileName, fileName]);

  const handleFileUpload = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const fileUploaded = event?.target?.files?.length
      ? event?.target?.files[0]
      : null;

    if (fileUploaded) {
      const validExtensions = allowedFileFormat.toLowerCase().split(',');
      const fileNameWithoutExtension =
        fileUploaded.name.substring(0, fileUploaded.name.lastIndexOf('.')) ||
        fileUploaded.name;
      const fileExtension = fileUploaded.name.split('.').pop()?.toLowerCase();
      if (fileUploaded.size > allowedFileSize) {
        const sizeMB = (allowedFileSize / (1024 * 1024)).toFixed(0);
        setError('filesize');
        try {
          enqueueSnackbar(
            `This file size exceed the maximum limit of ${sizeMB}MB.`,
            {
              variant: 'error',
            }
          );
        } catch {}
      } else if (
        !fileExtension ||
        !validExtensions.includes(`.${fileExtension}`)
      ) {
        try {
          enqueueSnackbar(alertMessage, {variant: 'error'});
        } catch {}
      } else {
        const normalizedFileName = `${fileNameWithoutExtension}.${fileExtension}`;
        setFileName(normalizedFileName);
        setFile(fileUploaded);
        setError('');
        handleFileConfirm && handleFileConfirm();
      }
    } else {
      // setFileName(NO_FILE_NAME);
    }
  };

  const handleClick = (event: SyntheticEvent) => {
    hiddenFileInput?.current?.click();
  };

  const checkAndDownloadFile = async () => {
    if (file) {
      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = url;
      a.download = sanitisedFileName;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    if (typeof file !== 'object') {
      if (error) {
        setLoading(false);
        return;
      }
      setError('nofile');
      setLoading(false);
      return;
    } else if (error) {
      setLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append(formFileName, file, fileName);
    try {
      await handleSubmit(formData);
      setLoading(false);
    } catch (error: any) {
      setError(error?.message);
      setLoading(false);
      return;
    }
  };

  useEffect(() => {
    if (submitFile) {
      (async () => {
        await onSubmit();
      })();
    }
  }, [submitFile, changeFile]);

  const updateFileFromUrl = async (fileUrl: string) => {
    const fileBlob = await getFileBlobFromUrl(fileUrl);
    setFile(fileBlob);
  };

  useEffect(() => {
    if (initialFile?.length) {
      setFileName(getFileNameFromFileUrl(initialFile));
      updateFileFromUrl(initialFile);
    }
  }, [initialFile]);

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" lineHeight={'18px'}>
          {title}
        </Typography>
      )}
      {initialFile != null ? (
        initialFile.length ? (
          <Box
            width={'100%'}
            display={'flex'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            py={{xxs: 1.875, md: 2}}
            px={{xxs: 1.875, md: 2.375}}
            border={'1px solid #E5E5E5'}
            borderRadius={8}
            mt={1}
            gap={1.375}
            sx={{
              ':hover': {
                borderColor: '#33333390',
              },
            }}
          >
            <Box
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              width={24}
              height={24}
            >
              <Image
                src={isAppIimjobs ? ResumeAttached : HiristResumeAttached}
                width={24}
                height={24}
                alt="resume"
              />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'flex-start'}
              alignItems={'flex-start'}
              gap={1.125}
              width={'100%'}
            >
              <Typography
                color="secondary.main"
                variant="body2"
                lineHeight="16px"
              >
                {loading ? (
                  profileStatus ? (
                    <Loader size={12} />
                  ) : (
                    <Box
                      sx={{display: 'flex', alignItems: 'center', gap: '4px'}}
                    >
                      <Loader size={12} />
                      <Typography sx={{fontSize: '12px'}}>
                        Parsing Your Resume...
                      </Typography>
                    </Box>
                  )
                ) : (
                  `${sanitisedFileName} ${
                    uploadTimeStamp?.length && uploadTimeStamp != 'Invalid Date'
                      ? `(Uploaded On: ${uploadTimeStamp})`
                      : ''
                  }`
                )}
              </Typography>
              <Box
                display={'flex'}
                justifyContent={'flex-start'}
                alignItems={'center'}
                gap={1}
              >
                <ButtonV2
                  component={'label'}
                  disableRipple
                  color="primary"
                  onClick={() => {
                    hiddenFileReInput?.current?.click();
                  }}
                  sx={{
                    border: '0px',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '20px',
                    padding: 0,
                    ':hover': {
                      backgroundColor: '#FFF',
                    },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {newUploadText}
                  <UploadInput
                    ref={hiddenFileReInput}
                    handleUpload={handleFileUpload}
                    accept={allowedFileFormat}
                  />
                </ButtonV2>
                <Typography
                  component={'label'}
                  color="primary.main"
                  variant="caption"
                  lineHeight="10px"
                >
                  |
                </Typography>
                <Typography
                  component={'label'}
                  color="primary.main"
                  variant="subtitle3"
                  lineHeight="16px"
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={checkAndDownloadFile}
                  whiteSpace="nowrap"
                >
                  {`${downloadText} (${formatBytes(file?.size ?? 0)})`}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              display="flex"
              mt={1}
              mb={1}
              alignItems={'center'}
              justifyContent={'flex-start'}
              gap={2.25}
            >
              <ButtonV2
                disableRipple
                variant="outlined"
                color="primary"
                component="label"
                onClick={handleClick}
                sx={{
                  justifyContent: 'flex-start',
                  width: {xxs: 180, md: 260},
                  height: 40,
                  textAlign: 'left',
                  cursor: 'pointer',
                  lineHeight: '16px',
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  padding: 1.5,
                  ':hover': {
                    textDecoration: 'none',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {buttonText}
                <UploadInput
                  ref={hiddenFileInput}
                  handleUpload={handleFileUpload}
                  accept={allowedFileFormat}
                />
              </ButtonV2>
              <Link
                variant="subtitle2"
                color={
                  !file && (error === 'nofile' || nofileError)
                    ? '#D11124 !important'
                    : '#7E7E7E !important'
                }
                onClick={checkAndDownloadFile}
                sx={{
                  padding: 0,
                  lineHeight: '16px',
                  ':hover': {
                    textDecoration: 'underline',
                    cursor: file ? 'pointer' : 'not-allowed',
                    color:
                      !file && (error === 'nofile' || nofileError)
                        ? '#D11124 !important'
                        : '#7E7E7E !important',
                  },
                }}
              >
                {loading ? (
                  <Loader
                    size={16}
                    sx={{
                      color: '#7E7E7E',
                    }}
                  />
                ) : (
                  `(${
                    file && fileName
                      ? defaultFileName?.length
                        ? defaultFileName
                        : fileName?.length > 15
                          ? truncateString(fileName, 15)
                          : fileName
                      : NO_FILE_NAME
                  })`
                )}
              </Link>
            </Box>
            <Typography
              variant="body3"
              color={error === 'filesize' ? 'error.main' : 'text.secondary'}
              lineHeight={'18px'}
            >
              {alertMessage}
            </Typography>
            {error && !['nofile', 'filesize'].includes(error) && (
              <Typography
                variant="body3"
                color="error"
                sx={{
                  marginLeft: 0.5,
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        )
      ) : (
        <Box
          width={'100%'}
          display={'flex'}
          justifyContent={'flex-start'}
          alignItems={'flex-start'}
          py={2}
          px={2.375}
          border={'1px solid #E5E5E5'}
          borderRadius={8}
          mt={1}
          gap={1.375}
          height={70}
        >
          <Skeleton variant="rounded" width={24} height={24}></Skeleton>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            gap={1.5}
            width={'100%'}
          >
            <Skeleton variant="rounded" width={'50%'} height={8}></Skeleton>
            <Skeleton variant="rounded" width={'100%'} height={10}></Skeleton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UploadFile;
